import React from "react";
import { Bike, Users, Gauge, Leaf } from "lucide-react";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface StatsData {
  bikeStats: {
    totalBikes: number;
    totalActiveBikes: number;
    totalDistance: number;
    averageDistancePerBike: number;
    totalCO2Offset: number;
  } | undefined;
  userStats: {
    totalUsers: number;
    totalAdmins: number;
    totalStandardUsers: number;
  } | undefined;
}

interface DashboardStatsProps {
  data: StatsData;
  isLoading: boolean;
}

const DashboardStats: React.FC<DashboardStatsProps> = ({ data, isLoading }) => {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total eBikes</CardTitle>
          <Bike className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-muted"></div>
            ) : (
              data.bikeStats?.totalBikes || 0
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isLoading ? (
              <div className="h-4 w-24 animate-pulse rounded bg-muted"></div>
            ) : (
              `${data.bikeStats?.totalActiveBikes || 0} eBikes active`
            )}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-muted"></div>
            ) : (
              data.userStats?.totalUsers || 0
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isLoading ? (
              <div className="h-4 w-24 animate-pulse rounded bg-muted"></div>
            ) : (
              `${data.userStats?.totalAdmins || 0} admins, ${
                data.userStats?.totalStandardUsers || 0
              } standard users`
            )}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total Offset-Eligible Distance</CardTitle>
          <Gauge className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-muted"></div>
            ) : (
              `${data.bikeStats?.totalDistance || 0} km`
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isLoading ? (
              <div className="h-4 w-24 animate-pulse rounded bg-muted"></div>
            ) : (
              `${data.bikeStats?.averageDistancePerBike || 0} km per eBike avg.`
            )}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">CO2 Offset</CardTitle>
          <Leaf className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {isLoading ? (
              <div className="h-8 w-16 animate-pulse rounded bg-muted"></div>
            ) : (
              `${data.bikeStats?.totalCO2Offset || 0} kg`
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Based on UK DEFRA conversion factors
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardStats;
